import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Loader2, CalendarPlus, Save } from "lucide-react";
import { adminApi } from "@/lib/adminApi";

const empty = { name: "", start_date: "", total_seats: 30, seats_left: 30, active: true };

export default function BatchManager() {
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(empty);
  const [creating, setCreating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.get("/admin/batches");
      setBatches(data);
    } catch {
      toast.error("Failed to load batches.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.start_date) {
      toast.error("Batch name and start date are required.");
      return;
    }
    setCreating(true);
    try {
      const { data } = await adminApi.post("/admin/batches", {
        ...form,
        total_seats: Number(form.total_seats),
        seats_left: Number(form.seats_left),
      });
      setBatches((b) => [...b, data].sort((a, z) => a.start_date.localeCompare(z.start_date)));
      setForm(empty);
      toast.success("Batch created");
    } catch {
      toast.error("Could not create batch");
    } finally {
      setCreating(false);
    }
  };

  const patchField = (id, field, value) =>
    setBatches((bs) => bs.map((b) => (b.id === id ? { ...b, [field]: value } : b)));

  const save = async (b) => {
    try {
      await adminApi.patch(`/admin/batches/${b.id}`, {
        name: b.name,
        start_date: b.start_date,
        total_seats: Number(b.total_seats),
        seats_left: Number(b.seats_left),
        active: b.active,
      });
      toast.success("Batch saved");
    } catch {
      toast.error("Could not save batch");
    }
  };

  const remove = async (id) => {
    try {
      await adminApi.delete(`/admin/batches/${id}`);
      setBatches((bs) => bs.filter((b) => b.id !== id));
      toast.success("Batch deleted");
    } catch {
      toast.error("Could not delete batch");
    }
  };

  const inputCls =
    "min-h-[42px] rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-900 placeholder:text-slate-400 focus:border-emerald-brand focus:outline-none";

  return (
    <div data-testid="batch-manager">
      {/* create form */}
      <form
        onSubmit={create}
        className="rounded-2xl border border-slate-200 bg-white p-5"
        data-testid="batch-create-form"
      >
        <p className="flex items-center gap-2 font-heading text-lg font-bold text-slate-900">
          <CalendarPlus size={18} className="text-emerald-600" /> Add a new batch
        </p>
        <p className="mt-1 text-sm text-slate-500">
          Batches appear on the landing page countdown automatically (soonest active batch shown).
        </p>
        <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
          <input
            data-testid="batch-name-input"
            className={`${inputCls} lg:col-span-2`}
            placeholder="Batch name (e.g. Weekend Batch)"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
          <input
            data-testid="batch-date-input"
            type="date"
            className={inputCls}
            value={form.start_date}
            onChange={(e) => setForm({ ...form, start_date: e.target.value })}
          />
          <input
            data-testid="batch-total-input"
            type="number"
            min="1"
            className={inputCls}
            placeholder="Total seats"
            value={form.total_seats}
            onChange={(e) => setForm({ ...form, total_seats: e.target.value })}
          />
          <input
            data-testid="batch-left-input"
            type="number"
            min="0"
            className={inputCls}
            placeholder="Seats left"
            value={form.seats_left}
            onChange={(e) => setForm({ ...form, seats_left: e.target.value })}
          />
        </div>
        <button
          type="submit"
          disabled={creating}
          data-testid="batch-create-btn"
          className="mt-4 flex min-h-[44px] items-center gap-2 rounded-xl bg-emerald-brand px-5 text-sm font-bold text-white transition-transform hover:scale-[1.02] disabled:opacity-70"
        >
          {creating ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />}
          Create Batch
        </button>
      </form>

      {/* list */}
      <div className="mt-5 space-y-3">
        {loading ? (
          <div className="flex items-center justify-center gap-2 py-10 text-slate-500">
            <Loader2 className="animate-spin" size={18} /> Loading batches…
          </div>
        ) : batches.length === 0 ? (
          <p className="rounded-2xl border border-dashed border-slate-300 bg-white py-10 text-center text-slate-500">
            No batches yet. Create one above to power the landing countdown.
          </p>
        ) : (
          batches.map((b) => (
            <div
              key={b.id}
              data-testid={`batch-row-${b.id}`}
              className="grid items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4 sm:grid-cols-2 lg:grid-cols-6"
            >
              <input
                className={`${inputCls} lg:col-span-2`}
                value={b.name}
                onChange={(e) => patchField(b.id, "name", e.target.value)}
              />
              <input
                type="date"
                className={inputCls}
                value={b.start_date}
                onChange={(e) => patchField(b.id, "start_date", e.target.value)}
              />
              <input
                type="number"
                className={inputCls}
                value={b.total_seats}
                onChange={(e) => patchField(b.id, "total_seats", e.target.value)}
                title="Total seats"
              />
              <input
                type="number"
                className={inputCls}
                value={b.seats_left}
                onChange={(e) => patchField(b.id, "seats_left", e.target.value)}
                title="Seats left"
              />
              <div className="flex items-center justify-end gap-2">
                <label className="flex items-center gap-1.5 text-xs font-semibold text-slate-600">
                  <input
                    type="checkbox"
                    checked={b.active}
                    onChange={(e) => patchField(b.id, "active", e.target.checked)}
                    className="h-4 w-4 accent-emerald-600"
                  />
                  Active
                </label>
                <button
                  onClick={() => save(b)}
                  data-testid={`batch-save-${b.id}`}
                  className="flex h-9 w-9 items-center justify-center rounded-lg bg-emerald-brand text-white transition-transform hover:scale-105"
                  title="Save"
                >
                  <Save size={16} />
                </button>
                <button
                  onClick={() => remove(b.id)}
                  data-testid={`batch-delete-${b.id}`}
                  className="flex h-9 w-9 items-center justify-center rounded-lg border border-red-200 bg-red-50 text-red-600 transition-colors hover:bg-red-100"
                  title="Delete"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
